import { useState, useEffect, useCallback } from 'react';

const footballSlides = [
  {
    src: 'https://public.readdy.ai/ai/img_res/fec4645b554daa42e91560a35c02bd64.jpg',
    caption: 'WR · University of Virginia',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=college%20football%20receiver%20making%20catch%20over%20defender%20end%20zone%20touchdown%20celebration%20stadium%20crowd%20athletic%20jersey&width=700&height=460&seq=football-slide-02&orientation=landscape',
    caption: 'Game Day · Scott Stadium',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=college%20football%20wide%20receiver%20running%20precise%20route%20on%20field%20practice%20athletic%20training%20jersey%20number%20sports%20photography&width=700&height=460&seq=football-slide-03&orientation=landscape',
    caption: 'Nebraska · Big Ten',
  },
];

function FootballSlideshow() {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);

  const goTo = useCallback((idx: number) => {
    setFading(true);
    setTimeout(() => {
      setCurrent(idx);
      setFading(false);
    }, 300);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % footballSlides.length);
    }, 3800);
    return () => clearInterval(timer);
  }, [current, goTo]);

  return (
    <div className="relative rounded-xl overflow-hidden mt-4 shadow-lg">
      <div
        className="transition-opacity duration-300"
        style={{ opacity: fading ? 0 : 1 }}
      >
        <img
          src={footballSlides[current].src}
          alt={footballSlides[current].caption}
          className="w-full h-48 object-cover object-top"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#2B6777]/90 to-transparent px-4 py-3">
          <p className="text-xs font-medium text-white/90">{footballSlides[current].caption}</p>
        </div>
      </div>
      {/* Dots */}
      <div className="absolute top-3 right-3 flex gap-1.5">
        {footballSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              i === current ? 'bg-white scale-125' : 'bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Team() {
  return (
    <section id="team" className="relative py-20 md:py-28 bg-gradient-to-b from-white via-[#52AB98]/5 to-white overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#52AB98]/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#F6D7DF]/15 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-[1200px] mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-6 border border-[#2B6777]/10 shadow-lg">
            <div className="w-2 h-2 rounded-full bg-[#52AB98] animate-pulse"></div>
            <span className="text-sm font-medium text-[#2B6777] uppercase tracking-wide">Our Team</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-heading font-bold text-[#2B6777] mb-4 tracking-tight">
            My Team
          </h2>
        </div>

        {/* ── Billy Kemp IV ── */}
        <div className="mb-16">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-[#2B6777]/5 hover:shadow-2xl transition-all duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">

              {/* Left: Headshot + Football Slideshow */}
              <div className="lg:col-span-1">
                <div className="relative">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src="https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/a7b48690-646f-49cf-8d4b-6d5f113081fc_baby-blu-dress-shirt.png?v=8d725e7a2c0583926e5d34f164e716df"
                      alt="Billy Kemp IV – Founder"
                      className="w-full h-auto object-cover object-top"
                      style={{ aspectRatio: '3/4' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2B6777]/30 via-transparent to-transparent"></div>
                  </div>

                  {/* Floating Badge */}
                  <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-xl p-4 border border-[#52AB98]/20">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2B6777] to-[#52AB98] flex items-center justify-center">
                        <i className="ri-trophy-line text-white text-sm"></i>
                      </div>
                      <span className="text-xs font-semibold text-[#2B6777]">Founder</span>
                    </div>
                  </div>

                  <div className="absolute -top-3 -left-3 w-16 h-16 bg-[#52AB98]/20 rounded-full blur-xl"></div>
                </div>

                {/* School Logos */}
                <div className="flex items-center justify-center gap-4 mt-8">
                  <div className="flex flex-col items-center gap-1">
                    <img
                      src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/15b5fa70ecaf327b8912d2de2e1c2b43.png"
                      alt="University of Virginia"
                      className="w-16 h-16 object-contain"
                    />
                    <span className="text-[10px] font-semibold text-[#2B6777]/60 uppercase tracking-wide">UVA</span>
                  </div>
                  <div className="w-px h-10 bg-[#2B6777]/15"></div>
                  <div className="flex flex-col items-center gap-1">
                    <img
                      src="https://readdy.ai/api/search-image?query=Nebraska%20Cornhuskers%20N%20logo%20bold%20red%20athletic%20emblem%20clean%20white%20background%20college%20football&width=100&height=100&seq=nebraska-logo-bk4&orientation=squarish"
                      alt="University of Nebraska"
                      className="w-16 h-16 object-contain"
                    />
                    <span className="text-[10px] font-semibold text-[#2B6777]/60 uppercase tracking-wide">Nebraska</span>
                  </div>
                </div>

                {/* Football Slideshow */}
                <FootballSlideshow />
              </div>

              {/* Right: Content */}
              <div className="lg:col-span-2">
                <div className="inline-flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2B6777] to-[#52AB98] flex items-center justify-center shadow-lg">
                    <i className="ri-user-star-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-[#2B6777]">Billy Kemp IV</h3>
                    <p className="text-sm text-[#52AB98] font-medium">Founder · Snapwindows.ai</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="relative pl-6 border-l-2 border-[#52AB98]/30">
                    <p className="text-lg md:text-xl text-[#2B6777] leading-relaxed">
                      "I grew up watching my father build Kemp Windows from the ground up — a Black-owned business that earned trust window by window for 25 years in Richmond. That example shaped everything I believe about how business should be done."
                    </p>
                  </div>

                  <div className="relative pl-6 border-l-2 border-[#2B6777]/30">
                    <p className="text-lg md:text-xl text-[#2B6777] leading-relaxed">
                      "After playing football at UVA and Nebraska, I came back with one mission: take the values I was raised on and build a company that could reach homeowners anywhere — without the pressure, without the games."
                    </p>
                  </div>

                  <div className="relative mt-8 p-6 md:p-8 bg-gradient-to-br from-[#52AB98]/10 to-[#2B6777]/5 rounded-2xl border-l-4 border-[#52AB98]">
                    <div className="absolute -top-3 left-6 w-10 h-10 rounded-full bg-gradient-to-br from-[#52AB98] to-[#2B6777] flex items-center justify-center shadow-lg">
                      <i className="ri-lightbulb-fill text-white text-lg"></i>
                    </div>
                    <p className="text-lg md:text-xl text-[#2B6777] leading-relaxed font-semibold pt-2">
                      "Snapwindows.ai exists because homeowners deserve a process built around their comfort — not a salesperson's commission."
                    </p>
                  </div>
                </div>

                {/* Video */}
                <div className="mt-10">
                  <p className="text-sm font-semibold text-[#2B6777]/60 uppercase tracking-wider mb-4">
                    Founder Video
                  </p>
                  <a
                    href="https://www.youtube.com/watch?v=KqkPCaXNotA&feature=youtu.be"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block relative rounded-2xl overflow-hidden shadow-xl group cursor-pointer"
                  >
                    <div className="relative aspect-video bg-[#2B6777]">
                      <img
                        src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/3b1b2d4726e801de18829e96880d86be.png"
                        alt="Why Snapwindows.ai Exists – Bill &amp; Billy Kemp IV"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/35 transition-all duration-300">
                        <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                          <i className="ri-play-fill text-[#2B6777] text-2xl ml-1"></i>
                        </div>
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-[#2B6777]/90 via-[#2B6777]/60 to-transparent">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#52AB98] animate-pulse"></div>
                        <span className="text-xs font-medium text-white/90 uppercase tracking-wide">Founder Story</span>
                      </div>
                      <h4 className="text-lg font-heading font-bold text-white">
                        Why Snapwindows.ai Exists
                      </h4>
                      <p className="text-sm text-white/75">Bill &amp; Billy Kemp IV share the vision</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Dad Billy (Bill Kemp) ── */}
        <div className="mb-16">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-[#2B6777]/5 hover:shadow-2xl transition-all duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">

              {/* Left: Photo */}
              <div className="lg:col-span-1">
                <div className="relative">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/af7ddcbdb2712a306ffcd2c60643919e.png"
                      alt="Bill Kemp – Father &amp; Mentor"
                      className="w-full h-auto object-cover object-top"
                      style={{ aspectRatio: '3/4' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2B6777]/30 via-transparent to-transparent"></div>
                  </div>

                  {/* Floating Badge */}
                  <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-xl p-4 border border-[#52AB98]/20">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2B6777] to-[#52AB98] flex items-center justify-center">
                        <i className="ri-building-line text-white text-sm"></i>
                      </div>
                      <span className="text-xs font-semibold text-[#2B6777]">25+ Years</span>
                    </div>
                  </div>

                  <div className="absolute -top-3 -left-3 w-16 h-16 bg-[#52AB98]/20 rounded-full blur-xl"></div>
                </div>

                {/* School Logo – Virginia */}
                <div className="flex justify-center mt-8">
                  <div className="flex flex-col items-center gap-1">
                    <img
                      src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/15b5fa70ecaf327b8912d2de2e1c2b43.png"
                      alt="University of Virginia"
                      className="w-24 h-24 object-contain"
                    />
                    <span className="text-[10px] font-semibold text-[#2B6777]/60 uppercase tracking-wide">Virginia</span>
                  </div>
                </div>
              </div>

              {/* Right: Content */}
              <div className="lg:col-span-2">
                <div className="inline-flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2B6777] to-[#52AB98] flex items-center justify-center shadow-lg">
                    <i className="ri-user-star-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-[#2B6777]">Bill Kemp</h3>
                    <p className="text-sm text-[#52AB98] font-medium">Father &amp; Mentor · Kemp Windows</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="relative pl-6 border-l-2 border-[#52AB98]/30">
                    <p className="text-lg md:text-xl text-[#2B6777] leading-relaxed">
                      "My father built Kemp Windows right here in Richmond, a proud Black-owned business that has served this community for over 25 years. I grew up watching what it takes to earn trust, stand behind your work, and do things the right way."
                    </p>
                  </div>

                  <div className="relative pl-6 border-l-2 border-[#2B6777]/30">
                    <p className="text-lg md:text-xl text-[#2B6777] leading-relaxed">
                      "Snapwindows.ai is my son's own path, a separate company we built together to scale those same values and work ethic to homeowners across the country."
                    </p>
                  </div>

                  <div className="relative mt-8 p-6 md:p-8 bg-gradient-to-br from-[#52AB98]/10 to-[#2B6777]/5 rounded-2xl border-l-4 border-[#52AB98]">
                    <div className="absolute -top-3 left-6 w-10 h-10 rounded-full bg-gradient-to-br from-[#52AB98] to-[#2B6777] flex items-center justify-center shadow-lg">
                      <i className="ri-heart-fill text-white text-lg"></i>
                    </div>
                    <p className="text-lg md:text-xl text-[#2B6777] leading-relaxed font-semibold pt-2">
                      "We didn't build Snapwindows.ai to replace integrity with technology. We built it to extend integrity through technology."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── KT ── */}
        <div className="mb-16">
          <div className="bg-gradient-to-br from-white to-[#F6D7DF]/10 rounded-3xl p-8 md:p-12 shadow-xl border border-[#2B6777]/5 hover:shadow-2xl transition-all duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">

              {/* Left: Photo */}
              <div className="lg:col-span-1">
                <div className="relative">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/167e4b4b71d53e8cd8f05a8eb106cd5f.png"
                      alt="KT – Team Member"
                      className="w-full h-auto object-cover object-top"
                      style={{ aspectRatio: '3/4' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2B6777]/30 via-transparent to-transparent"></div>
                  </div>

                  {/* Floating Badge */}
                  <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-xl p-4 border border-[#F6D7DF]/30">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F6D7DF] to-[#52AB98] flex items-center justify-center">
                        <i className="ri-graduation-cap-fill text-white text-sm"></i>
                      </div>
                      <span className="text-xs font-semibold text-[#2B6777]">M.Ed · UVA</span>
                    </div>
                  </div>

                  <div className="absolute -top-3 -left-3 w-16 h-16 bg-[#F6D7DF]/30 rounded-full blur-xl"></div>
                </div>

                {/* School Logos – MSU + UVA */}
                <div className="flex items-center justify-center gap-4 mt-8">
                  <div className="flex flex-col items-center gap-1">
                    <img
                      src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/126ad9117c7d0b402f24b93e453dab31.png"
                      alt="Mississippi State University"
                      className="w-16 h-16 object-contain"
                    />
                    <span className="text-[10px] font-semibold text-[#2B6777]/60 uppercase tracking-wide">Miss. State</span>
                  </div>
                  <div className="w-px h-10 bg-[#2B6777]/15"></div>
                  <div className="flex flex-col items-center gap-1">
                    <img
                      src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/15b5fa70ecaf327b8912d2de2e1c2b43.png"
                      alt="University of Virginia"
                      className="w-16 h-16 object-contain"
                    />
                    <span className="text-[10px] font-semibold text-[#2B6777]/60 uppercase tracking-wide">UVA</span>
                  </div>
                </div>
              </div>

              {/* Right: Content */}
              <div className="lg:col-span-2">
                <div className="inline-flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F6D7DF] to-[#52AB98] flex items-center justify-center shadow-lg">
                    <i className="ri-user-heart-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-[#2B6777]">KT</h3>
                    <p className="text-sm text-[#52AB98] font-medium">Team Member · Miss. State &amp; UVA Graduate</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="relative pl-6 border-l-2 border-[#F6D7DF]/50">
                    <p className="text-lg md:text-xl text-[#2B6777] leading-relaxed">
                      "I met Billy (Our Founder) playing football during my first year at UVA; he was an outstanding player and an even better person. From day one I knew that he was someone I could trust, both on the field and off."
                    </p>
                  </div>

                  <div className="relative pl-6 border-l-2 border-[#52AB98]/30">
                    <p className="text-lg md:text-xl text-[#2B6777] leading-relaxed">
                      "While at UVA I earned my masters in Higher Education, but more importantly as students we vowed to uphold the Honor Code and have carried that same dignity over to our business."
                    </p>
                  </div>

                  <div className="relative pl-6 border-l-2 border-[#2B6777]/30">
                    <p className="text-lg md:text-xl text-[#2B6777] leading-relaxed">
                      "My favorite things in life are learning, teaching, and helping. Here at SnapWindows I get to do all these things while positively impacting people's lives."
                    </p>
                  </div>

                  <div className="relative mt-8 p-6 md:p-8 bg-gradient-to-br from-[#F6D7DF]/20 to-[#52AB98]/10 rounded-2xl border-l-4 border-[#F6D7DF]">
                    <div className="absolute -top-3 left-6 w-10 h-10 rounded-full bg-gradient-to-br from-[#F6D7DF] to-[#D98CA3] flex items-center justify-center shadow-lg">
                      <i className="ri-heart-fill text-white text-lg"></i>
                    </div>
                    <p className="text-lg md:text-xl text-[#2B6777] leading-relaxed font-semibold pt-2">
                      "I'm extremely proud to make a real difference in people's lives by improving their homes and ensuring they're treated fairly every step of the way."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
